import Stripe from 'stripe';
import { z } from 'zod';

import { FeatureFlags } from './FeatureFlags';
import { PermissionComponent, PermissionProject, PermissionTeam } from './Permission.schema';
import {
    DesignerSubscriptionSchema,
    SubscriptionBillingCycleSchema,
    SubscriptionChangeSchema,
    SubscriptionDataSchema,
} from './Subscription';

export const TeamInfoSchema = z.object({
    id: z.string(),
    name: z.string(),
    permissions: z.array(
        z.enum([
            PermissionProject.ALL,
            PermissionProject.ADMIN,
            PermissionProject.VIEW,
            PermissionProject.EDIT,
            PermissionProject.DELETE,
            PermissionComponent.ALL,
            PermissionComponent.VIEW,
            PermissionComponent.EDIT,
            PermissionTeam.OWNER,
            PermissionTeam.ADMIN,
        ]),
    ),
    subscription: DesignerSubscriptionSchema,
});

export type TeamInfo = z.infer<typeof TeamInfoSchema>;

export const TeamUserSchema = z.object({
    id: z.string(),
    team: z.string(),
    user: z.string(),
    permissions: z.array(
        z.enum([
            PermissionProject.ALL,
            PermissionProject.ADMIN,
            PermissionProject.VIEW,
            PermissionProject.EDIT,
            PermissionProject.DELETE,
            PermissionComponent.ALL,
            PermissionComponent.VIEW,
            PermissionComponent.EDIT,
            PermissionTeam.OWNER,
            PermissionTeam.ADMIN,
            PermissionTeam.USER,
        ]),
    ),
    createdAt: z.string(),
});

export type TeamUser = z.infer<typeof TeamUserSchema>;

export const TeamSchema = z.object({
    id: z.string(),
    name: z.string().min(3, 'Team name must be at least 3 characters long'),
    secret: z.string().default(''),
    users: z.object({ docs: z.union([TeamUserSchema, z.string()]).array() }).optional(),
    updatedAt: z.string(), // TODO
    createdAt: z.string(), // TODO
    createdBy: z.string(),
    subscriptions: SubscriptionDataSchema.array(),
    subscriptionBillingCycle: SubscriptionBillingCycleSchema,
    subscriptionUpdateNotice: z
        .object({
            date: z.number(),
            changes: SubscriptionChangeSchema.array(),
        })
        .nullable(),
    subscriptionCancelsAt: z.number().optional(),
    subscriptionPastDue: z.boolean().optional(),
    stripeCustomerId: z.string().optional(),
    stripeSubscription: z.custom<Stripe.Subscription>().optional(),
    creditBalance: z.number().default(0),
    hideCredits: z.boolean().optional(),

    inAppSupport: z.boolean().optional(),
    designAIAssistantGuidelines: z.string().default(''),
    hideSuggestions: z.boolean().optional(),
    hideAI: z.boolean().optional(),
    forceWorkspace: z.string().nullable().optional(),
    flags: z.array(z.nativeEnum(FeatureFlags)).optional(),
});

// *1: Zod & Typescript don't like circular dependencies..

export type Team = z.infer<typeof TeamSchema>;

export type MergeTeamsResult = {
    original: { id: string; name: string };
    contributing: { id: string; name: string };
    blockers: string[];
    addedMembers: number;
    switchedActiveUsers: number;
    movedDocuments: Record<string, number>;
    merged: boolean;
};
